import React, { useEffect, useState, useCallback } from "react";
import { wasteApi } from "@/api/waste";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import QuickLoader from "@/components/shared/QuickLoader";
import { Trash2, Euro, Scale, Hash, TrendingUp } from "lucide-react";

const formatEur = (v) => `${(v || 0).toFixed(2)} €`;
const formatQty = (v) => `${(v || 0).toFixed(2)}`;

function KpiCard({ icon: Icon, label, value, sub, tone = "primary" }) {
  const tones = {
    primary: "bg-[#7B2D3F]/10 text-[#7B2D3F]",
    green: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700",
  };
  return (
    <Card className="p-4 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tones[tone]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {sub && <p className="text-xs text-gray-500">{sub}</p>}
      </div>
    </Card>
  );
}

function BarChart({ data, valueKey = "total_cost", labelKey = "key", title, formatter = formatEur }) {
  if (!data || data.length === 0) {
    return <p className="text-sm text-gray-400 italic py-4">Sense dades</p>;
  }
  const max = Math.max(...data.map((d) => d[valueKey] || 0), 1);
  return (
    <div className="space-y-2">
      {title && <p className="text-sm font-semibold text-gray-700 mb-2">{title}</p>}
      {data.slice(0, 8).map((d, i) => (
        <div key={`${d[labelKey]}-${i}`} className="flex items-center gap-2 text-sm">
          <span className="w-32 truncate text-gray-700">{d[labelKey] || "—"}</span>
          <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
            <div
              className="bg-[#7B2D3F] h-full rounded-full transition-all"
              style={{ width: `${((d[valueKey] || 0) / max) * 100}%` }}
            />
          </div>
          <span className="text-xs font-semibold text-gray-900 w-20 text-right">{formatter(d[valueKey])}</span>
        </div>
      ))}
    </div>
  );
}

function DailySeriesChart({ data }) {
  if (!data || data.length === 0) {
    return <p className="text-sm text-gray-400 italic py-4">Sense dades</p>;
  }
  const max = Math.max(...data.map((d) => d.total_cost || 0), 1);
  return (
    <div className="flex items-end gap-1 h-32 mt-2 overflow-x-auto pb-2">
      {data.map((d) => (
        <div key={d.date} className="flex flex-col items-center min-w-[18px] flex-1">
          <div
            className="w-full bg-[#7B2D3F]/80 hover:bg-[#7B2D3F] rounded-t transition-all"
            style={{ height: `${((d.total_cost || 0) / max) * 100}%` }}
            title={`${d.date}: ${formatEur(d.total_cost)}`}
          />
          <span className="text-[9px] text-gray-500 mt-1 rotate-45 origin-top-left whitespace-nowrap">
            {d.date.slice(5)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function WasteDashboard({ venueId }) {
  const [days, setDays] = useState("30");
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const k = await wasteApi.getKpis(venueId, parseInt(days, 10));
      setKpis(k);
    } catch (e) {
      console.error("KPIs error", e);
    } finally {
      setLoading(false);
    }
  }, [venueId, days]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <QuickLoader />;
  if (!kpis) return null;

  return (
    <div className="space-y-6" data-testid="waste-dashboard">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Període: <strong>{kpis.date_from}</strong> → <strong>{kpis.date_to}</strong>
        </p>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40" data-testid="waste-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últims 7 dies</SelectItem>
            <SelectItem value="30">Últims 30 dies</SelectItem>
            <SelectItem value="90">Últims 90 dies</SelectItem>
            <SelectItem value="365">Últim any</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={Euro} label="Cost total" value={formatEur(kpis.totals.total_cost)} tone="primary" />
        <KpiCard icon={Scale} label="Quantitat total" value={formatQty(kpis.totals.total_quantity)} sub="suma de kg/L/u" tone="amber" />
        <KpiCard icon={Hash} label="Registres" value={kpis.totals.count} tone="blue" />
        <KpiCard icon={TrendingUp} label="Mitjana per registre" value={formatEur(kpis.totals.avg_cost_per_record)} tone="green" />
      </div>

      <Card className="p-4">
        <p className="text-sm font-semibold text-gray-700 mb-1">Evolució diària (cost €)</p>
        <DailySeriesChart data={kpis.daily_series} />
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-4"><BarChart data={kpis.by_family} title="Cost per família" /></Card>
        <Card className="p-4"><BarChart data={kpis.by_reason} title="Cost per motiu" /></Card>
        <Card className="p-4"><BarChart data={kpis.by_zone} title="Cost per zona" /></Card>
        <Card className="p-4">
          <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-[#7B2D3F]" /> Top 10 productes
          </p>
          <BarChart data={kpis.top_products.map((p) => ({ key: p.product_name, total_cost: p.total_cost }))} />
        </Card>
      </div>
    </div>
  );
}
